import { PreviewInvoiceDialog } from "@jugl-web/domain-resources/invoices/components/PreviewInvoiceDialog";
import { LoadingSpinner } from "@jugl-web/ui-components/cross-platform/LoadingSpinner";
import { DataLoadingWrapper } from "@jugl-web/ui-components/cross-platform/content/DataLoadingWrapper";
import { TableGrid } from "@jugl-web/ui-components/cross-platform/content/TableGrid";
import { FC } from "react";
import { InvoicesBulkActions } from "./components/InvoicesBulkActions";
import { InvoicesInitialList } from "./components/InvoicesInitialList";
import { TableNoSearchResults } from "./components/TableNoSearchResults";
import { InvoicesTableProvider } from "./context/InvoicesTableProvider";
import { useInvoicesTableContext } from "./context/useInvoicesTableContext";
import { useInvoiceTableColumns } from "./hooks";
import { ComponentProps } from "./types";

export const InvoicesTableContent: FC<{ emptyContent?: JSX.Element }> = ({
  emptyContent,
}) => {
  const {
    items,
    loadMoreInvoices,
    isLoading,
    isInitialLoading,
    hasMore,
    isInitialized,
    searchQuery,
    filters,
    addItem,
    totalEntries,
    entity,
    config,
    isGuest,
    actions,
    onNavigate,
    availableActions,
  } = useInvoicesTableContext();
  const { columns, previewInvoice, setPreviewInvoice } =
    useInvoiceTableColumns();
  const hasFiltersOrSearch =
    searchQuery || Object.keys(filters || {}).length > 0;
  const hasInvoices = Boolean(items?.length);

  const getTableState = () => {
    if (isInitialLoading) {
      return "loading";
    }
    if (totalEntries === 0 && !hasFiltersOrSearch && isInitialized) {
      return "empty";
    }

    if (!hasInvoices && hasFiltersOrSearch) {
      return "no-results";
    }

    return "normal";
  };

  const renderContent = () => {
    const state = getTableState();
    if (state === "empty") {
      return emptyContent || <InvoicesInitialList />;
    }

    const tableData = state === "normal" ? items || [] : [];
    const onReachEnd =
      state === "normal" ? () => hasMore && loadMoreInvoices() : () => {};
    return (
      <DataLoadingWrapper isLoading={isInitialLoading}>
        <TableGrid
          id="invoices"
          variant="grid"
          data={tableData}
          onReachEnd={onReachEnd}
          columns={columns}
          noCellPadding
          emptyContent={
            state === "no-results" ? (
              <TableNoSearchResults />
            ) : (
              emptyContent || <InvoicesInitialList />
            )
          }
          bottomContent={
            !isInitialLoading && isLoading ? (
              <div className="flex w-full items-center justify-center py-10">
                <LoadingSpinner />
              </div>
            ) : undefined
          }
        />
      </DataLoadingWrapper>
    );
  };

  return (
    <>
      {renderContent()}
      <InvoicesBulkActions />
      {previewInvoice && (
        <PreviewInvoiceDialog
          entityLogo={entity?.img || ""}
          entityName={entity?.name || ""}
          globalConfig={config}
          isOpen={!!previewInvoice}
          onClose={() => setPreviewInvoice(undefined)}
          invoiceData={previewInvoice}
          displayPlace="table"
          onActionSuccess={(action, invoice) => {
            if (action === "convert" && invoice) {
              addItem(invoice);
            }
          }}
          isReadOnly={isGuest}
          actions={actions}
          onNavigate={onNavigate}
          availableActions={availableActions}
        />
      )}
    </>
  );
};

export const InvoicesTable: FC<ComponentProps> = (props) => {
  const { emptyContent, variant, ...commonProps } = props;

  if (variant === "task") {
    const { taskId } = props;
    return (
      <InvoicesTableProvider {...commonProps} variant="task" taskId={taskId}>
        <InvoicesTableContent emptyContent={emptyContent} />
      </InvoicesTableProvider>
    );
  }

  if (variant === "customer") {
    const { customerId } = props;
    return (
      <InvoicesTableProvider
        {...commonProps}
        variant="customer"
        customerId={customerId}
      >
        <InvoicesTableContent emptyContent={emptyContent} />
      </InvoicesTableProvider>
    );
  }

  return (
    <InvoicesTableProvider {...commonProps} variant="all">
      <InvoicesTableContent emptyContent={emptyContent} />
    </InvoicesTableProvider>
  );
};
